import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ATypeMap,
  CTypeBreadCrumb,
  ATypeNationalMeritSummary,
  FullWidthTwinButton,
  ATypeNationalMeritDescription,
  GOI_WEB_URL,
  customEvent,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code;
  let crematorium;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];

      description = `강원장례정보공개 (고이장례연구소) - ${crematorium.companyname} | 국가유공자는 화장 준비는 어떻게 해야 할까요? 국가유공자 장례 준비, 강원장례정보공개(고이장례연구소)와 함께 준비하세요.`;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 장례 준비, ${district} 국가유공자 장례 비용`,
      pageOgTitle: `${crematorium?.companyname}에서의 국가유공자를 위한 화장 비용에 관한 정보를 안내합니다 :: 강원장례정보공개 (고이장례연구소)`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function VeteransCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "gangwon",
    eventPath: "veterans_crema",
  });
  const sidoName = FUNERAL_SIDO.get(Number(defaultCrema.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultCrema.sido))?.get(
    Number(defaultCrema.sigungu)
  );
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gangwon-portal.co.kr/gangwon-veterans/crema/${res.companyname}`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <CTypeBreadCrumb
        firstItem={{
          text: `국가유공자 장례 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "veterans_crema",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `국가유공자 장례헬프센터`,
          href: `/gangwon-veterans/`,
          event: {
            domain: "gangwon",
            path: "veterans_crema",
            trigger_and_target: "br_crumb_veterans",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "veterans_crema",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultCrema}
        title={`${defaultCrema.companyname}`}
        managed_type={defaultCrema.managed_type}
        isHwajang
      />

      <Stack
        gap="12px"
        padding="10px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "gangwon",
                path: "veterans_crema",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",

            onClick: () => {
              customEvent({
                domain: "gangwon",
                path: "veterans_crema",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeMap defaultParlor={defaultCrema} />
        <ATypeNationalMeritDescription />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h3 className="button_16_sb">강원 국가유공자 화장장</h3>
        {regionCrematoriums.map((res) => (
          <BTypeCardView
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/gangwon-veterans/crema/${res.companyname}/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "gangwon",
              path: "veterans_crema",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
